/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2026 Prodeo - All Rights Reserved
 */

import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { i18n } from '@umanforge/common';
import type { ChartWithDataApi } from 'interfaces/chartWithData.interface';
import { AxisReadStartPrefix, getIndicatorLabel, getWeekPrefix } from '../../constants/chartDisplay.constants';

dayjs.extend( isoWeek );

const PREFIX_PERIOD_KEY: Record<string, string> = {
  'd': 'period_days', 'w': 'period_weeks', 'm': 'period_months', 'a': 'period_years', 'y': 'period_years',
};

// Calcule une valeur max "agréable" pour les jauges, en arrondissant la valeur max réelle à un chiffre significatif suivi de 0, 2 ou 5, puis en multipliant par le nombre de segments souhaité
const niceGaugeMax = ( rawMax: number, splitNumber: number ): number => {
  const rawStep = rawMax / splitNumber;
  const magnitude = Math.pow( 10, Math.floor( Math.log10( rawStep ) ) );
  const normalized = rawStep / magnitude;
  const niceStep = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceStep * magnitude * splitNumber;
};

const getPeriodLabel = ( readStart: string, delta: number | null ): string | null => {
  if ( delta === null ) { return null; }
  const key = PREFIX_PERIOD_KEY[ readStart.charAt( 0 ) ];
  if ( !key ) { return null; }
  return i18n.t( 'translation:over_x_periods', { count: delta, period: i18n.t( 'translation:' + key, { count: delta } ) } );
};

const getAxisFormatter = ( readStart: string ) => {
  const prefix = readStart.charAt( 0 ) as AxisReadStartPrefix;

  switch ( prefix ) {
  case AxisReadStartPrefix.Day:
    return ( value: string ) => {return dayjs( value ).format( 'DD/MM/YYYY' );};

  case AxisReadStartPrefix.Week:
  case AxisReadStartPrefix.Sprint:
    return ( value: string ) => {
      const match = value.match( /^(\d{4})-W(\d{2})$/ );
      const weekNum = match
        ? parseInt( match[ 2 ], 10 )
        : dayjs( value ).isoWeek();
      const year = match
        ? match[ 1 ]
        : String( dayjs( value ).isoWeekYear() );

      return `${getWeekPrefix()} ${weekNum} ${year}`;
    };

  case AxisReadStartPrefix.Month:
    return ( value: string ) => {return dayjs( value ).format( 'MMM YYYY' );};

  case AxisReadStartPrefix.Annual:
  case AxisReadStartPrefix.Year:
    return ( value: string ) => {return dayjs( value ).format( 'YYYY' );};

  default:
    return ( value: string ) => {return value;};
  }
};

type ChartDisplayProps = {
  chart: ChartWithDataApi;
  onPing?: () => void;
};
type CustomContainer = HTMLDivElement & { __customCleanup?: () => void };


const ChartDisplay: React.FC<ChartDisplayProps> = ( { chart } ) => {
  const containerRef = useRef<HTMLDivElement>( null );

  useEffect( () => {
    let myChart: echarts.ECharts | null = null;
    let rafId: number | null = null;
    const handleResize = () => {
      if ( rafId !== null ) {cancelAnimationFrame( rafId );}
      rafId = requestAnimationFrame( () => {
        if ( myChart ) {myChart.resize();}
      } );
    };
    if ( !containerRef.current ) {return;}
    echarts.dispose( containerRef.current );
    if (
      containerRef.current.clientWidth > 0 &&
      containerRef.current.clientHeight > 0
    ) {
      const sortedDates = Object.keys( chart.datas ).sort();
      const values = sortedDates.map( d => {return chart.datas[ d ].value;} );
      const targets = sortedDates.map( d => {return chart.datas[ d ].target;} );
      const backendMaxVal = chart.max?.value;
      const additionals =
        chart.additionalIndicator !== 'none'
          ? sortedDates.map( d => {
            const entry = chart.datas[ d ];
            return (
              ( entry as Record<string, number | null> )[
                chart.additionalIndicator
              ] ?? null
            );
          } )
          : null;

      const unit = chart.chartUnit?.value ?? '';
      const isAutoIncrement = chart.type === 'auto_increment_gauge';
      const isGauge = chart.type === 'progress_gauge' || chart.type === 'custom_gauge' || isAutoIncrement;

      if ( isGauge ) {
        const latestDate = sortedDates[ sortedDates.length - 1 ];
        const latestValue = latestDate != null ? ( chart.datas[ latestDate ]?.value ?? 0 ) : 0;
        const nonNullValues = values.filter( ( v ): v is number => { return v !== null; } );
        const dataMax = nonNullValues.length > 0 ? Math.max( ...nonNullValues ) : 100;
        const target = chart.targetValue != null && chart.targetValue > 0 ? chart.targetValue : null;

        let declaredMax = typeof backendMaxVal === 'number' ? backendMaxVal : dataMax;
        const latestMaybe = latestValue ?? 0;
        if ( latestMaybe >= declaredMax ) {
          const padding = Math.max( 1, Math.round( latestMaybe * 0.05 ) );
          declaredMax = latestMaybe + padding;
        }

        const rawGaugeMax = target != null
          ? Math.max( target * 1.25, declaredMax * 1.1 )
          : Math.max( declaredMax * 1.25, 100 );
        const gaugeMax = isAutoIncrement ? niceGaugeMax( rawGaugeMax, 12 ) : niceGaugeMax( rawGaugeMax, 5 );
        const isProgress = chart.type === 'progress_gauge' || isAutoIncrement;
        if ( isAutoIncrement ) {
          if ( containerRef.current ) {
            containerRef.current.innerHTML = '';
            const container = containerRef.current;

            myChart = echarts.init( container );

            const mainColor = chart.color || '#FFAB91';
            const overlayColor = '#FD7347';
            const option = {
              backgroundColor: '#FFF',
              title: {
                text: `${chart.title}${unit ? ' (' + unit + ')' : ''}`,
                subtextStyle: { fontSize: 11, color: '#64748b' },
                textStyle: { fontSize: 13, fontWeight: 600, color: '#1e293b' },
                left: 4,
                top: 4,
              },
              series: [
                {
                  type: 'gauge',
                  center: [ '50%', '60%' ],
                  startAngle: 200,
                  endAngle: -20,
                  min: 0,
                  max: gaugeMax,
                  splitNumber: 12,
                  itemStyle: { color: mainColor },
                  progress: { show: true, width: 30 },
                  pointer: { show: false },
                  axisLine: { lineStyle: { width: 30 } },
                  axisTick: { distance: -45, splitNumber: 5, lineStyle: { width: 2, color: '#999' } },
                  splitLine: { distance: -52, length: 14, lineStyle: { width: 3, color: '#999' } },
                  axisLabel: { distance: -20, color: '#999', fontSize: 12, formatter: ( v: number ) => { return Math.round( v ).toLocaleString(); } },
                  anchor: { show: false },
                  title: { show: false },
                  detail: {
                    valueAnimation: true,
                    width: '60%',
                    lineHeight: 40,
                    borderRadius: 8,
                    offsetCenter: [ 0, '-15%' ],
                    fontSize: 24,
                    fontWeight: 'bolder',
                    formatter: ( v: number ) => {return `${Math.round( v ?? 0 ).toLocaleString()}${unit ? '\u00a0' + unit : ''}`;},
                    color: 'inherit',
                  },
                  data: [ { value: latestValue ?? 0 } ],
                },
                {
                  type: 'gauge',
                  center: [ '50%', '60%' ],
                  startAngle: 200,
                  endAngle: -20,
                  min: 0,
                  max: gaugeMax,
                  itemStyle: { color: overlayColor },
                  progress: { show: true, width: 8 },
                  pointer: { show: false },
                  axisLine: { show: false },
                  axisTick: { show: false },
                  splitLine: { show: false },
                  axisLabel: { show: false },
                  detail: { show: false },
                  data: [ { value: latestValue ?? 0 } ],
                },
              ],
              animationDuration: 900,
            } as echarts.EChartsOption;

            myChart.setOption( option );

            const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver( () => { myChart?.resize(); } ) : null;
            if ( ro ) { ro.observe( container ); }

            ( container as CustomContainer ).__customCleanup = () => {
              ro?.disconnect();
              myChart?.dispose();
              if ( containerRef.current ) { containerRef.current.innerHTML = ''; }
            };

            return () => {
              const cleanup = ( containerRef.current as CustomContainer )?.__customCleanup;
              if ( typeof cleanup === 'function' ) { cleanup(); }
            };
          }
        }

        const gaugeColorStops: [ number, string ][] = target != null && !isProgress
          ? ( chart.targetType === 'min'
            ? [ [ target / gaugeMax, '#fca5a5' ], [ 1, '#86efac' ] ]
            : [ [ target / gaugeMax, '#86efac' ], [ 1, '#fca5a5' ] ] )
          : [ [ 1, '#e2e8f0' ] ];

        myChart = echarts.init( containerRef.current );
        ( containerRef.current as CustomContainer ).__customCleanup = () => {
          myChart?.dispose();
        };

        myChart!.setOption( {
          backgroundColor: '#FFF',
          title: {
            text: `${chart.title}${unit ? ' (' + unit + ')' : ''}`,
            textStyle: { fontSize: 13, fontWeight: 600, color: '#1e293b' },
            left: 4,
            top: 4,
          },
          series: [
            {
              type: 'gauge',
              center: [ '50%', '60%' ],
              radius: '70%',
              min: 0,
              max: gaugeMax,
              splitNumber: 5,
              progress: isProgress ? { show: true, width: isAutoIncrement ? 20 : 16 } : { show: false },
              pointer: isProgress
                ? { show: false }
                : { length: '60%', width: 6, itemStyle: { color: chart.color } },
              axisLine: {
                lineStyle: {
                  width: isAutoIncrement ? 20 : ( isProgress ? 16 : 12 ),
                  color: isAutoIncrement
                    ? ( [ [ Math.min( 1, latestValue / gaugeMax ), chart.color ], [ 1, '#e6eef6' ] ] as [ number, string ][] )
                    : isProgress
                      ? ( [ [ 1, '#e2e8f0' ] ] as [ number, string ][] )
                      : gaugeColorStops,
                },
              },
              axisTick: { distance: isProgress ? -24 : 0, splitNumber: 5 },
              splitLine: { distance: isProgress ? -24 : 0, length: isProgress ? 24 : 10 },
              detail: isAutoIncrement
                ? {
                  valueAnimation: true,
                  formatter: ( value: number ) => { return `${Math.round( value ?? 0 ).toLocaleString()}${unit ? '\u00a0' + unit : ''}`; },
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#0f172a',
                  offsetCenter: [ 0, '5%' ],
                }
                : {
                  valueAnimation: true,
                  formatter: `{value}${unit ? '\u00a0' + unit : ''}`,
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#1e293b',
                  offsetCenter: [ 0, '75%' ],
                },
              title: isAutoIncrement
                ? {
                  fontSize: 12,
                  color: '#64748b',
                  offsetCenter: [ 0, '55%' ],
                }
                : {
                  fontSize: 12,
                  color: '#64748b',
                  offsetCenter: [ 0, '105%' ],
                },
              animationDuration: isAutoIncrement ? 900 : undefined,
              data: [ { value: latestValue, name: chart.dataName } ],
            } as echarts.SeriesOption,
          ],
        } );
      } else {
        const isLine =
          chart.type === 'basic_line' ||
          chart.type === 'area_line' ||
          chart.type === 'step_line';

        const seriesData: echarts.SeriesOption[] = [
          {
            name: chart.dataName,
            type: isLine ? 'line' : 'bar',
            data: values,
            itemStyle: { color: chart.color },
            areaStyle:
              chart.type === 'area_line'
                ? { color: chart.color + '33' }
                : undefined,
            step: chart.type === 'step_line' ? 'middle' : undefined,
            barWidth: isLine ? undefined : '50%',
          },
        ];

        if (
          targets.some( t => {return t !== null;} ) &&
          chart.targetValue !== null
        ) {
          seriesData.push( {
            name: chart.targetName || 'Cible',
            type: 'line',
            step: 'middle',
            data: targets,
            lineStyle: {
              color: chart.targetColor,
              type: 'dashed',
              width: 2,
            },
            itemStyle: { color: chart.targetColor },
            symbol: 'none',
          } );
        }

        if ( additionals ) {
          seriesData.push( {
            name: getIndicatorLabel( chart.additionalIndicator ),
            type: 'line',
            data: additionals,
            lineStyle: {
              color: '#94a3b8',
              type: 'dotted',
              width: 2,
            },
            itemStyle: { color: '#94a3b8' },
            symbol: 'circle',
            symbolSize: 4,
          } );
        }

        if ( !myChart ) { myChart = echarts.init( containerRef.current ); }
        myChart!.setOption( {
          backgroundColor: '#FFF',
          title: {
            text: `${chart.title}${unit ? ' (' + unit + ')' : ''}`,
            textStyle: {
              fontSize: 13,
              fontWeight: 600,
              color: '#1e293b',
            },
            left: 4,
            top: 4,
          },
          tooltip: {
            trigger: 'axis',
            backgroundColor: '#fff',
            borderColor: '#ccc',
            textStyle: { color: '#222' },
            formatter: ( params: echarts.TooltipComponentFormatterCallbackParams ) => {
              type TooltipParam = { axisValue?: unknown; marker?: string; seriesName?: string; value?: unknown };
              const paramArray = ( Array.isArray( params ) ? params : [ params ] ) as TooltipParam[];
              if ( paramArray.length === 0 ) { return ''; }
              const dateStr = String( paramArray[ 0 ].axisValue ?? '' );
              const formattedDate = getAxisFormatter( chart.readStart )( dateStr );
              const trendLabel = chart.additionalIndicator === 'trend' ? getIndicatorLabel( 'trend' ) : null;
              const lines = paramArray
                .filter( p => { return p.seriesName !== trendLabel; } )
                .map( p => {
                  const rawVal = p.value !== null && p.value !== undefined ? p.value : null;
                  const val = rawVal !== null ? ( typeof rawVal === 'number' ? rawVal.toFixed( 1 ).replace( /\.0$/, '' ) : rawVal ) : '-';
                  return `${p.marker}${p.seriesName}: <b>${val}</b>${unit ? '\u00a0' + unit : ''}`;
                } );
              return `<b>${formattedDate}</b><br/>${lines.join( '<br/>' )}`;
            },
          },
          legend: {
            type: 'scroll',
            top: 26,
            textStyle: { fontSize: 11 },
          },
          grid: {
            left: '5%',
            right: '5%',
            top: 68,
            bottom: 30,
            containLabel: true,
          },
          xAxis: {
            type: 'category',
            data: sortedDates,
            axisLine: { lineStyle: { color: '#888' } },
            axisLabel: {
              rotate: 30,
              fontSize: 10,
              color: '#333',
              formatter: getAxisFormatter( chart.readStart ),
            },
          },
          yAxis: {
            type: 'value',
            axisLine: { lineStyle: { color: '#888' } },
            axisLabel: {
              color: '#333',
            },
            splitLine: {
              lineStyle: { color: '#eee', type: 'dashed' },
            },
          },
          series: seriesData,
        } );
      }

      const resizeObserver =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver( handleResize )
          : null;

      if ( resizeObserver && containerRef.current ) {
        resizeObserver.observe( containerRef.current );
      } else {
        window.addEventListener( 'resize', handleResize );
      }

      return () => {
        resizeObserver?.disconnect();
        window.removeEventListener( 'resize', handleResize );
        if ( rafId !== null ) {cancelAnimationFrame( rafId );}
        const cleanup = ( containerRef.current as CustomContainer )?.__customCleanup;
        if ( typeof cleanup === 'function' ) { cleanup(); }
        else { myChart?.dispose(); }
      };
    }
  }, [ chart ] );

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="w-full h-[260px] sm:h-[300px] md:h-[340px] lg:h-[380px]"
      />

      { ( chart.isUsedMin || chart.isUsedMax ) && ( () => {
        const nonNullValues = Object.values( chart.datas )
          .map( d => { return d.value; } )
          .filter( ( v ): v is number => { return v !== null; } );
        const minRaw = nonNullValues.length ? Math.min( ...nonNullValues ) : null;
        const maxRaw = nonNullValues.length ? Math.max( ...nonNullValues ) : null;
        const unit = chart.chartUnit?.value ?? '';
        const periodLabel = getPeriodLabel( chart.readStart, chart.deltaMinMax );
        const displayedMin = typeof chart.min?.value === 'number'
          ? Math.round( chart.min.value )
          : ( minRaw !== null ? Math.round( minRaw ) : null );
        const displayedMax = typeof chart.max?.value === 'number'
          ? Math.round( chart.max.value )
          : ( maxRaw !== null ? Math.round( maxRaw ) : null );
        return (
          <div className="absolute top-1 right-2 flex flex-col items-end gap-0.5">
            <div className="flex gap-1 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-md px-2 py-1 shadow-sm">
              { chart.isUsedMin && (
                <span className="flex items-center gap-0.5 text-[11px] text-gray-600">
                  <span className="text-[9px] font-bold uppercase text-gray-400 tracking-wide">{i18n.t( 'translation:chart_min_abbr' )}</span>
                  { displayedMin !== null
                    ? <span className="font-semibold text-gray-800">{displayedMin}{unit ? `\u00a0${unit}` : ''}</span>
                    : <span className="font-semibold text-gray-400">--</span>
                  }
                </span>
              ) }
              { chart.isUsedMin && chart.isUsedMax && (
                <span className="text-gray-300 text-[11px]">/</span>
              ) }
              { chart.isUsedMax && (
                <span className="flex items-center gap-0.5 text-[11px] text-gray-600">
                  <span className="text-[9px] font-bold uppercase text-gray-400 tracking-wide">{i18n.t( 'translation:chart_max_abbr' )}</span>
                  { displayedMax !== null
                    ? <span className="font-semibold text-gray-800">{displayedMax}{unit ? `\u00a0${unit}` : ''}</span>
                    : <span className="font-semibold text-gray-400">--</span>
                  }
                </span>
              ) }
            </div>
            { periodLabel && (
              <span className="text-[10px] text-gray-400 italic">{periodLabel}</span>
            ) }
          </div>
        );
      } )() }
    </div>
  );
};

export default ChartDisplay;